import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Plus, Calendar, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Prescriptions = ({ baby }) => {
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    prescription_type: '',
    title: '',
    description: '',
    date_issued: '',
    doctor_name: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPrescriptions();
  }, [baby]);

  const fetchPrescriptions = async () => {
    try {
      const response = await axios.get(`${API}/prescriptions/${baby.id}`);
      const sortedPrescriptions = response.data.sort((a, b) => new Date(b.date_issued) - new Date(a.date_issued));
      setPrescriptions(sortedPrescriptions);
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
      toast.error('Failed to load prescriptions and reports');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrescription = async (e) => {
    e.preventDefault();
    
    if (!formData.prescription_type || !formData.title || !formData.description || !formData.date_issued) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const prescriptionData = {
        baby_id: baby.id,
        prescription_type: formData.prescription_type,
        title: formData.title,
        description: formData.description,
        date_issued: formData.date_issued,
        doctor_name: formData.doctor_name || null
      };
      
      await axios.post(`${API}/prescriptions`, prescriptionData);
      toast.success(`${formData.prescription_type === 'prescription' ? 'Prescription' : 'Report'} added successfully!`);
      setShowAddDialog(false);
      setFormData({
        prescription_type: '',
        title: '',
        description: '',
        date_issued: '',
        doctor_name: ''
      });
      fetchPrescriptions();
    } catch (error) {
      console.error('Error adding prescription:', error);
      toast.error('Failed to add prescription/report');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    // If already in DD/MM/YYYY format, return as is
    if (dateString && dateString.includes('/')) {
      return dateString;
    }
    // Otherwise convert from ISO format
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getTypeBadge = (type) => {
    return type === 'prescription' ? (
      <Badge variant="outline" className="bg-blue-100 text-blue-800">Prescription</Badge>
    ) : (
      <Badge variant="outline" className="bg-green-100 text-green-800">Report</Badge>
    );
  };

  const getTypeIcon = (type) => {
    return (
      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
        type === 'prescription' ? 'bg-blue-100' : 'bg-green-100'
      }`}>
        <FileText className={`w-6 h-6 ${
          type === 'prescription' ? 'text-blue-600' : 'text-green-600'
        }`} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading prescriptions and reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white hover:bg-opacity-20"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Prescription & Reports</h1>
                <p className="text-blue-200 text-sm">{baby.name}</p>
              </div>
            </div>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white"
                data-testid="add-prescription-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Record
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add Prescription or Report</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddPrescription} className="space-y-4">
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select onValueChange={(value) => setFormData(prev => ({ ...prev, prescription_type: value }))} required>
                    <SelectTrigger data-testid="prescription-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prescription">Prescription</SelectItem>
                      <SelectItem value="report">Medical Report</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    placeholder="Enter title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    data-testid="prescription-title-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe the prescription or report details"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    data-testid="prescription-description-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date_issued">Date Issued *</Label>
                  <Input
                    id="date_issued"
                    type="date"
                    value={formData.date_issued}
                    onChange={(e) => setFormData(prev => ({ ...prev, date_issued: e.target.value }))}
                    max={new Date().toISOString().split('T')[0]}
                    data-testid="prescription-date-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="doctor_name">Doctor Name (Optional)</Label>
                  <Input
                    id="doctor_name"
                    placeholder="Enter doctor's name"
                    value={formData.doctor_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, doctor_name: e.target.value }))}
                    data-testid="prescription-doctor-input"
                  />
                </div>
                
                <div className="flex space-x-2 pt-4">
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={submitting}
                    data-testid="save-prescription-button"
                  >
                    {submitting ? 'Saving...' : 'Save Record'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => setShowAddDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="px-6 py-6">
        {prescriptions.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center" data-testid="no-prescriptions">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Records</h3>
            <p className="text-gray-600 mb-4">No prescriptions or reports recorded for {baby.name}.</p>
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-first-prescription-button">
              <Plus className="w-4 h-4 mr-2" />
              Add First Record
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {prescriptions.map((prescription) => (
              <div key={prescription.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid={`prescription-${prescription.id}`}>
                <div className="flex items-start space-x-4">
                  {getTypeIcon(prescription.prescription_type)}
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                          {prescription.title}
                        </h3>
                        {getTypeBadge(prescription.prescription_type)}
                      </div>
                      
                      <div className="text-sm text-gray-500 flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(prescription.date_issued)}</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 leading-relaxed mb-3">{prescription.description}</p>
                    
                    {prescription.doctor_name && (
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <User className="w-4 h-4" />
                        <span>Dr. {prescription.doctor_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Prescriptions;